import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Search, MessageSquare, Download, Loader2, UserPlus, Save, X, ChevronDown, ChevronUp, Cake } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useShowMore } from '../../components/useShowMore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const cleanPhone = (p) => (p || '').replace(/[^0-9+]/g, '');

const escapeCsv = (s) => {
    const v = String(s ?? '');
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

const ContactsAdminPanel = () => {
    const [data, setData] = useState({ count: 0, rows: [], missing: [], missing_count: 0, birthdays_this_month: [] });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showMissing, setShowMissing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [savingId, setSavingId] = useState(null);

    const fetchContacts = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/customer-contacts`, { withCredentials: true });
            setData(data || { count: 0, rows: [], missing: [], missing_count: 0, birthdays_this_month: [] });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load contacts');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchContacts(); }, [fetchContacts]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return data.rows;
        return data.rows.filter(
            r => r.name.toLowerCase().includes(q)
              || r.phone.toLowerCase().includes(q)
        );
    }, [data.rows, search]);

    const {
        visibleItems: visibleContacts,
        ShowMoreButton: ContactsShowMore,
        total: contactsTotal,
        limit: contactsLimit,
    } = useShowMore(filtered, 10, 10);

    const {
        visibleItems: visibleMissing,
        ShowMoreButton: MissingShowMore,
        total: missingTotal,
        limit: missingLimit,
    } = useShowMore(data.missing || [], 10, 10);

    const exportCsv = () => {
        const header = 'Name,Phone\n';
        const body = filtered
            .map(r => `${escapeCsv(r.name)},${escapeCsv(r.phone)}`)
            .join('\n');
        const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `betohalics-contacts-${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filtered.length} contact${filtered.length === 1 ? '' : 's'}`);
    };

    const startEdit = (id) => {
        setEditingId(id);
        setEditingValue('');
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditingValue('');
    };
    const savePhone = async (userId) => {
        const phone = editingValue.trim();
        if (!phone) {
            toast.error('Enter a phone number');
            return;
        }
        setSavingId(userId);
        try {
            await axios.post(`${API}/admin/user-phone`,
                { user_id: userId, phone },
                { withCredentials: true });
            toast.success('Phone added');
            cancelEdit();
            await fetchContacts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save phone');
        }
        setSavingId(null);
    };

    return (
        <div className="card-dark p-5 mb-4" data-testid="contacts-admin-panel">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-[#00FFFF]/15 flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#00FFFF]" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-white">Customer Contacts</h3>
                        <p className="text-[11px] text-[#A0AAB2]">
                            Active players with a phone on file
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40">
                        {data.count} total · {filtered.length} shown
                    </span>
                    <Button
                        size="sm"
                        onClick={exportCsv}
                        disabled={filtered.length === 0}
                        data-testid="contacts-export-csv"
                        className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                    >
                        <Download className="w-3.5 h-3.5 mr-1" />
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* Birthdays this month */}
            {data.birthdays_this_month && data.birthdays_this_month.length > 0 && (
                <div
                    className="rounded-lg p-3 mb-3 border"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255,0,127,0.10), rgba(255,140,0,0.06))',
                        borderColor: 'rgba(255,0,127,0.30)',
                    }}
                    data-testid="birthdays-this-month"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Cake className="w-4 h-4 text-[#FF007F]" />
                        <p className="text-[11px] font-bold tracking-[0.15em] text-[#FF007F]">
                            BIRTHDAYS THIS MONTH ({data.birthdays_this_month.length})
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {data.birthdays_this_month.map((b) => {
                            const tel = cleanPhone(b.phone);
                            const dayLabel = b.today
                                ? 'TODAY'
                                : b.days_until > 0
                                    ? `in ${b.days_until}d`
                                    : `${Math.abs(b.days_until)}d ago`;
                            return (
                                <div
                                    key={b.id}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0a0a0a] border"
                                    style={{
                                        borderColor: b.today
                                            ? 'rgba(57,255,20,0.55)'
                                            : b.passed
                                                ? 'rgba(160,170,178,0.20)'
                                                : 'rgba(255,215,0,0.40)',
                                        boxShadow: b.today ? '0 0 14px rgba(57,255,20,0.30)' : 'none',
                                    }}
                                    data-testid={`birthday-${b.id}`}
                                >
                                    <span className="text-xs font-bold text-white">{b.name}</span>
                                    <span className="text-[10px] text-[#A0AAB2]">
                                        turns <span className="text-[#FFD700] font-bold">{b.turns}</span> · {dayLabel}
                                    </span>
                                    <a
                                        href={`sms:${tel}?body=Happy birthday from BETohalics! Enjoy 25 free spins on us today.`}
                                        title="Send birthday SMS"
                                        className="w-6 h-6 rounded-full bg-[#FFD700]/15 text-[#FFD700] hover:bg-[#FFD700]/30 flex items-center justify-center"
                                        data-testid={`birthday-sms-${b.id}`}
                                    >
                                        <MessageSquare className="w-3 h-3" />
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                <Input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or phone…"
                    className="pl-9 bg-[#0a0a0a] border-white/10 text-white"
                    data-testid="contacts-search"
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 text-[#00FFFF] animate-spin" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-[#A0AAB2] text-sm">
                    {data.count === 0
                        ? 'No active players with a phone number on file yet.'
                        : 'No contacts match that search.'}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto">
                    {visibleContacts.map((r) => {
                        const tel = cleanPhone(r.phone);
                        return (
                            <div
                                key={r.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#0a0a0a] border border-white/5 hover:border-[#00FFFF]/40 transition-colors"
                                data-testid={`contact-row-${r.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-white truncate">{r.name}</p>
                                    <p className="text-[11px] text-[#A0AAB2] truncate font-mono">{r.phone}</p>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <a
                                        href={`sms:${tel}`}
                                        title="Text"
                                        className="w-8 h-8 rounded-full bg-[#FFD700]/15 text-[#FFD700] hover:bg-[#FFD700]/25 flex items-center justify-center transition-colors"
                                        data-testid={`contact-sms-${r.id}`}
                                    >
                                        <MessageSquare className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Show-more for contacts */}
            {filtered.length > 0 && (
                <>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(contactsLimit, contactsTotal)} of {contactsTotal}
                    </p>
                    <ContactsShowMore testid="contacts-show-more" />
                </>
            )}

            {/* Missing-phone backfill section */}
            {data.missing_count > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                    <button
                        type="button"
                        onClick={() => setShowMissing((v) => !v)}
                        className="text-xs text-[#FFD700] inline-flex items-center gap-1 hover:underline font-bold"
                        data-testid="missing-phones-toggle"
                    >
                        {showMissing ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        <UserPlus className="w-3 h-3" />
                        {data.missing_count} active player{data.missing_count === 1 ? '' : 's'} missing a phone — add now
                    </button>

                    {showMissing && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                            {visibleMissing.map((m) => (
                                <div
                                    key={m.id}
                                    className="flex items-center gap-2 p-2.5 rounded-lg bg-[#0a0a0a] border border-[#FFD700]/15"
                                    data-testid={`missing-row-${m.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-white truncate">{m.name}</p>
                                        <p className="text-[10px] text-[#A0AAB2] truncate">{m.email}</p>
                                    </div>
                                    {editingId === m.id ? (
                                        <div className="flex items-center gap-1">
                                            <Input
                                                autoFocus
                                                type="tel"
                                                placeholder="+1 555 …"
                                                value={editingValue}
                                                onChange={(e) => setEditingValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') savePhone(m.id);
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                className="h-7 w-32 bg-[#050505] border-white/10 text-white text-xs"
                                                data-testid={`missing-input-${m.id}`}
                                            />
                                            <Button
                                                size="sm"
                                                onClick={() => savePhone(m.id)}
                                                disabled={savingId === m.id}
                                                className="h-7 w-7 p-0 bg-[#39FF14] text-black hover:bg-[#39FF14]/80"
                                                data-testid={`missing-save-${m.id}`}
                                            >
                                                {savingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={cancelEdit}
                                                className="h-7 w-7 p-0 bg-white/10 text-white hover:bg-white/20"
                                                data-testid={`missing-cancel-${m.id}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            size="sm"
                                            onClick={() => startEdit(m.id)}
                                            className="h-7 px-2 text-[10px] bg-[#FFD700]/15 text-[#FFD700] hover:bg-[#FFD700]/25 border border-[#FFD700]/40"
                                            data-testid={`missing-add-${m.id}`}
                                        >
                                            <UserPlus className="w-3 h-3 mr-1" />
                                            Add
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {showMissing && (
                        <>
                            <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                                Showing {Math.min(missingLimit, missingTotal)} of {missingTotal}
                            </p>
                            <MissingShowMore testid="missing-phones-show-more" />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContactsAdminPanel;
